import { useCallback, useEffect, useMemo } from "react";

import { yupResolver } from "@hookform/resolvers/yup";
import { useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";

import {
  excludeHintedFields,
  getEmptyFormValues,
} from "src/cases/caseView/formUtils";
import {
  useAvailableCaseTasks,
  useCaseTaskFormSchema,
  useGetCaseTemplateTask,
} from "src/cases/caseView/hooks";
import { toastCaseError } from "src/cases/caseView/toastCaseError";
import { casesKeys, useSaveCaseTaskForm } from "src/cases/queries/cases";
import type { CaseDeserialized, CaseTask } from "src/cases/queries/casesTypes";
import {
  TaskIdExtractor,
  transformCaseTask,
} from "src/cases/queries/casesTypes";
import type { CaseTemplateTaskOutput } from "src/clients/case-management";
import { toastActions } from "src/design-system/Toast/utils";
import { DashboardPageParams } from "src/router/urls";
import { logger } from "src/utils/logger";
import { useParamsDecode } from "src/utils/useParamsDecode";

type UseTaskFormStateParams = {
  initialTask: CaseTask;
  fallbackTemplate: CaseTemplateTaskOutput;
  caseData?: CaseDeserialized;
  fieldHints: Record<string, unknown> | undefined;
};

export const useTaskFormState = ({
  initialTask,
  fallbackTemplate,
  caseData,
  fieldHints,
}: UseTaskFormStateParams) => {
  const caseId = caseData?.data.id;
  const availableTasks = useAvailableCaseTasks(caseData);
  const getTemplate = useGetCaseTemplateTask(caseData);

  const task = useMemo(() => {
    const updatedTask = availableTasks.find((t) => t.id === initialTask.id);
    const transformed = updatedTask
      ? transformCaseTask(updatedTask, getTemplate)
      : initialTask;

    if (
      JSON.stringify(transformed.formValues) ===
        JSON.stringify(initialTask.formValues) &&
      transformed.isCompleted === initialTask.isCompleted
    ) {
      return initialTask;
    }
    return transformed;
  }, [availableTasks, getTemplate, initialTask]);

  const template = getTemplate(task.key) || fallbackTemplate;

  const schema = useCaseTaskFormSchema(template);
  const { mutateAsync: saveTask, isPending } = useSaveCaseTaskForm(caseId);
  const queryClient = useQueryClient();
  const { wsId } = useParamsDecode<DashboardPageParams>();

  const defaultValues = useMemo(() => {
    const fields = getEmptyFormValues(template?.form?.fields);

    return {
      ...fields,
      ...task.formValues,
      ...(fieldHints ?? {}),
    };
  }, [template?.form?.fields, task.formValues, fieldHints]);

  const form = useForm<Record<string, unknown>>({
    mode: "onChange",
    defaultValues,
    resolver: yupResolver(schema),
  });

  const { isDirty, isValid } = form.formState;

  const isFormEmpty = useMemo(() => {
    return (
      template?.form?.fields?.every((field) => {
        const value = defaultValues[field.key];
        return field.type === "multi_select"
          ? Array.isArray(value) && value.length === 0
          : value == null;
      }) ?? true
    );
  }, [defaultValues, template?.form?.fields]);

  useEffect(() => {
    form.reset(defaultValues, { keepDirtyValues: true });
  }, [defaultValues, form]);

  const saveForm = useCallback(
    async (formEtagOverride?: number, options?: { saveHints?: boolean }) => {
      if (!task.formMeta?.id) {
        logger.warn(`Task form ID is missing for task ${task.key}`);
        return;
      }

      const currentValues = form.getValues();
      const valuesToSave =
        fieldHints && !options?.saveHints
          ? excludeHintedFields(
              currentValues,
              fieldHints,
              task.formValues,
              template?.form?.fields,
            )
          : currentValues;

      form.reset(valuesToSave, { keepValues: true });

      try {
        const validatedValues = schema.cast(valuesToSave, {
          stripUnknown: true,
        }) as Record<string, unknown>;

        await saveTask({
          taskKey: TaskIdExtractor.getTaskKey(task.id),
          fullTaskId: task.id,
          formEtag: formEtagOverride ?? task.formMeta?.etag ?? 0,
          taskFormId: task.formMeta.id,
          taskFormValues: validatedValues,
        });
      } catch (error) {
        form.reset(currentValues, { keepValues: true, keepDirty: true });
        toastCaseError(
          error,
          "Failed to save task form",
          "The task has been modified elsewhere. Please reload and try again.",
        );
        throw error;
      }
    },
    [form, task, schema, saveTask, fieldHints, template],
  );

  const clearForm = useCallback(
    async (
      rejectHints?: (emptyValues: Record<string, unknown>) => Promise<void>,
    ) => {
      const emptyValues = getEmptyFormValues(template?.form?.fields);

      const previousValues = form.getValues();
      form.reset(emptyValues, { keepDirty: true });

      if (rejectHints) {
        try {
          await rejectHints(emptyValues);
        } catch {
          form.reset(previousValues, { keepDirty: true });
          toastActions.failure({
            title: "Failed to clear form",
            description: "Could not reject hints. Please try again.",
          });
          return;
        }
      }

      try {
        await saveForm(undefined, { saveHints: true });
      } catch {
        // The server still holds the previous values, so show them again.
        form.reset(previousValues, { keepDirty: true });
        if (rejectHints && caseId) {
          void queryClient.invalidateQueries({
            queryKey: casesKeys.get(wsId, caseId),
          });
        }
      }
    },
    [form, template?.form?.fields, saveForm, queryClient, wsId, caseId],
  );

  const canSave = !isPending && isDirty && isValid;

  return {
    task,
    template,
    form,
    isDirty,
    isPending,
    isFormEmpty,
    canSave,
    saveForm,
    clearForm,
  };
};
